import { useEffect, useRef, useState } from "react";
import { Download, ImageIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const W = 1080;
const FORMATS = {
  story: { h: 1920, logoY: 170, brandY: 360, overY: 580, nameY: 700, nameSize: 82, priceSize: 60, detailSize: 42, phoneOff: 270, siteOff: 195, gradY: 520 },
  square: { h: 1080, logoY: 60, brandY: 240, overY: 360, nameY: 460, nameSize: 68, priceSize: 52, detailSize: 36, phoneOff: 170, siteOff: 100, gradY: 400 },
};

const THEMES = {
  standard: { bg: "#0a0a0a", glow: "255,121,0", accent: "#FF7900", badge: "" },
  rentree: { bg: "#0a1022", glow: "64,140,255", accent: "#5BA8FF", badge: "🎒 SPÉCIAL RENTRÉE" },
  blackfriday: { bg: "#000000", glow: "212,175,55", accent: "#FBBC04", badge: "🖤 BLACK FRIDAY" },
  fetes: { bg: "#170b0b", glow: "229,72,77", accent: "#FF5B63", badge: "🎄 FÊTES DE FIN D'ANNÉE", snow: true },
  ete: { bg: "#08161a", glow: "0,190,210", accent: "#2DD4BF", badge: "☀️ OFFRES D'ÉTÉ" },
};

const detectSeason = () => {
  const d = new Date();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if ((m === 8 && day >= 15) || m === 9) return "rentree";
  if (m === 11 && day >= 20) return "blackfriday";
  if (m === 12) return "fetes";
  if (m === 7 || (m === 8 && day < 15)) return "ete";
  return "standard";
};

const wrap = (ctx, text, maxW) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const tst = line ? `${line} ${w}` : w;
    if (ctx.measureText(tst).width > maxW && line) {
      lines.push(line);
      line = w;
    } else line = tst;
  }
  if (line) lines.push(line);
  return lines;
};

export const PromoPost = ({ offers }) => {
  const { lang, t } = useLang();
  const p = t.dash.promoPost;
  const canvasRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);
  const active = offers.filter((o) => o.active);
  const best = active.find((o) => o.promo_until && o.promo_until >= today) || active[0];
  const [sel, setSel] = useState("");
  const [fmt, setFmt] = useState("story");
  const [themeSel, setThemeSel] = useState("auto");
  const themeKey = themeSel === "auto" ? detectSeason() : themeSel;
  const th = THEMES[themeKey];
  const offer = active.find((o) => o.id === sel) || best;
  const L = FORMATS[fmt];
  const H = L.h;

  useEffect(() => {
    if (!offer || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = th.bg;
    ctx.fillRect(0, 0, W, H);
    const g = ctx.createRadialGradient(W / 2, L.gradY, 50, W / 2, L.gradY, 950);
    g.addColorStop(0, `rgba(${th.glow},0.22)`);
    g.addColorStop(1, `rgba(${th.glow},0)`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    if (th.snow) {
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      for (let i = 0; i < 60; i++) {
        const x = (i * 173) % W;
        const yy = (i * 293) % H;
        ctx.beginPath();
        ctx.arc(x, yy, 2 + (i % 3), 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.save();
    ctx.translate(W / 2 - 20 * 2.4, L.logoY);
    ctx.scale(2.4, 2.4);
    ctx.fillStyle = "#FF7900";
    ctx.beginPath();
    ctx.arc(20, 20, 20, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(20, 20, 15, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.moveTo(22.5, 9.5);
    ctx.lineTo(13.5, 22);
    ctx.lineTo(19, 22);
    ctx.lineTo(17.5, 30.5);
    ctx.lineTo(26.5, 18);
    ctx.lineTo(21, 18);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    ctx.textAlign = "center";
    ctx.fillStyle = "#ffffff";
    ctx.font = "600 46px Arial";
    ctx.fillText("Smart Telecom Advisor", W / 2, L.brandY);

    if (th.badge) {
      ctx.fillStyle = th.accent;
      ctx.font = "700 34px Arial";
      ctx.fillText(th.badge, W / 2, L.overY - 62);
    }

    ctx.fillStyle = th.accent;
    ctx.font = "700 38px Arial";
    ctx.fillText(p.overlineText, W / 2, L.overY);

    let y = L.nameY;
    ctx.fillStyle = "#ffffff";
    ctx.font = `700 ${L.nameSize}px Arial`;
    wrap(ctx, offer.name, 920).forEach((l) => {
      ctx.fillText(l, W / 2, y);
      y += L.nameSize + 16;
    });

    y += 20;
    ctx.fillStyle = th.accent;
    ctx.font = `700 ${L.priceSize}px Arial`;
    wrap(ctx, offer.price, 920).forEach((l) => {
      ctx.fillText(l, W / 2, y);
      y += L.priceSize + 16;
    });

    y += 24;
    ctx.fillStyle = "rgba(255,255,255,0.65)";
    ctx.font = `400 ${L.detailSize}px Arial`;
    wrap(ctx, lang === "fr" ? offer.detail_fr : offer.detail_en, 880).forEach((l) => {
      ctx.fillText(l, W / 2, y);
      y += L.detailSize + 16;
    });

    if (offer.promo_until && offer.promo_until >= today) {
      ctx.fillStyle = "#34d399";
      ctx.font = "600 36px Arial";
      ctx.fillText(
        `${p.validUntil} ${new Date(offer.promo_until).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", { day: "numeric", month: "long" })}`,
        W / 2,
        y + 50
      );
    }

    ctx.fillStyle = th.accent;
    ctx.font = "700 48px Arial";
    ctx.fillText("+32 473 22 33 44", W / 2, H - L.phoneOff);
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "400 40px Arial";
    ctx.fillText("www.smartelecomadvisor.be", W / 2, H - L.siteOff);
  }, [offer, lang, p, today, fmt, H, L, th]);

  if (!offer) return null;

  const caption = offer
    ? [
        th.badge ? `${th.badge}\n` : null,
        `🔥 ${p.overlineText} 🔥`,
        "",
        `🧡 ${offer.name}`,
        `💶 ${offer.price}`,
        `✅ ${lang === "fr" ? offer.detail_fr : offer.detail_en}`,
        offer.promo_until && offer.promo_until >= today
          ? `⏳ ${p.validUntil} ${new Date(offer.promo_until).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", { day: "numeric", month: "long" })}`
          : null,
        "",
        lang === "fr" ? "📞 +32 473 22 33 44 — je m'occupe de tout !" : "📞 +32 473 22 33 44 — I take care of everything!",
        "👉 https://www.smartelecomadvisor.be",
      ]
        .filter((l) => l !== null)
        .join("\n")
    : "";

  const copyCaption = async () => {
    await navigator.clipboard.writeText(caption);
    toast.success(p.copied);
  };

  const download = () => {
    canvasRef.current.toBlob((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url;
      a.download = `promo-${fmt}-${today}.png`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="border border-white/10 bg-[#111111] p-6 md:p-8" data-testid="promo-post-generator">
      <h3 className="flex items-center gap-2 font-display font-semibold mb-1">
        <ImageIcon className="w-4 h-4 text-[#FF7900]" /> {p.title}
      </h3>
      <p className="text-sm text-white/40 mb-6">{p.subtitle}</p>
      <div className="flex flex-col lg:flex-row gap-8 lg:items-start">
        <div className="flex-1 space-y-4">
          <select
            value={offer.id}
            onChange={(e) => setSel(e.target.value)}
            data-testid="promo-post-select"
            className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300 cursor-pointer"
          >
            {active.map((o) => (
              <option key={o.id} value={o.id} className="bg-[#111]">
                {o.name} — {o.price}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => setFmt("story")}
              data-testid="promo-format-story"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-300 ${fmt === "story" ? "border-[#FF7900] bg-[#FF7900]/15 text-[#FF7900]" : "border-white/15 text-white/50 hover:border-white/40"}`}
            >
              {p.fmtStory}
            </button>
            <button
              onClick={() => setFmt("square")}
              data-testid="promo-format-square"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-300 ${fmt === "square" ? "border-[#FF7900] bg-[#FF7900]/15 text-[#FF7900]" : "border-white/15 text-white/50 hover:border-white/40"}`}
            >
              {p.fmtSquare}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setThemeSel("auto")}
              data-testid="promo-theme-auto"
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-300 ${themeSel === "auto" ? "border-[#FF7900] bg-[#FF7900]/15 text-[#FF7900]" : "border-white/15 text-white/50 hover:border-white/40"}`}
            >
              {p.themes.auto} ({p.themes[detectSeason()]})
            </button>
            {Object.keys(THEMES).map((k) => (
              <button
                key={k}
                onClick={() => setThemeSel(k)}
                data-testid={`promo-theme-${k}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-300 ${themeSel === k ? "border-[#FF7900] bg-[#FF7900]/15 text-[#FF7900]" : "border-white/15 text-white/50 hover:border-white/40"}`}
              >
                {p.themes[k]}
              </button>
            ))}
          </div>
          <button
            onClick={download}
            data-testid="promo-post-download"
            className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            <Download className="w-4 h-4" /> {p.download}
          </button>
          <p className="text-xs text-white/30">{fmt === "story" ? p.hint : p.hintSquare}</p>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/40 mb-2">{p.captionTitle}</p>
            <textarea
              readOnly
              value={caption}
              rows={9}
              data-testid="promo-post-caption"
              className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-xs text-white/70 outline-none resize-none leading-relaxed"
            />
            <button
              onClick={copyCaption}
              data-testid="promo-post-copy"
              className="mt-2 inline-flex items-center gap-2 border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] text-xs px-4 py-2 rounded-full transition-colors duration-300"
            >
              <Copy className="w-3.5 h-3.5" /> {p.copy}
            </button>
          </div>
        </div>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          data-testid="promo-post-canvas"
          className={`${fmt === "square" ? "w-[300px]" : "w-[240px]"} rounded-xl border border-white/10 shrink-0 shadow-2xl`}
        />
      </div>
    </div>
  );
};
